import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './Dashboard.css';
import { toast } from 'react-toastify';
import { GoogleMap } from '@react-google-maps/api';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';
import AddDevice from './AddDevice';
import AssignedRoute from './AssignedRoute';
import TripReport from './TripReport';
import Geofencing from './Geofencing';
import Alerts from './Alerts';
import DeviceMonitor from './DeviceMonitor';
import Maintenance from './Maintenance';
import Support from './Support';
import RelayControl from './RelayControl';
import Devices from './Devices';
import CANTelemetry from './CANTelemetry';
import { DocumentUploader } from './Document';
import MileEdgeLogo from '../assets/mileedge.png';
import MileEdgeTextLogo from '../assets/Milledgetext.png';
import TruckIconImg from '../assets/truckicon.png';
import {
  MdHome,
  MdDevices,
  MdRoute,
  MdAssignment,
  MdPowerSettingsNew,
  MdLocationOn,
  MdNotifications,
  MdMonitorHeart,
  MdBuild,
  MdDescription,
  MdSupportAgent,
  MdLogout,
  MdSignalCellularAlt,
} from "react-icons/md";

const ALERT_TYPES = {
  RT: 'Rash Turn',
  BD: 'Battery Disconnect',
  HA: 'Harsh Acceleration',
  HB: 'Harsh Braking',
  BL: 'Battery Low'
};

// Stable reference (module-level, never recreated) — GoogleMap treats
// `center` as a controlled prop and re-syncs the map to it whenever the
// object reference changes. An inline literal here would fight against
// any imperative fitBounds()/setCenter() calls on every re-render.
const DEFAULT_MAP_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_MAP_ZOOM = 5;

const ALERT_TYPE_ORDER = ['RT', 'BD', 'HA', 'HB', 'BL'];

const ALERT_COLORS = {
  RT: '#4c1d95',
  BD: '#1d4ed8',
  HA: '#0f766e',
  HB: '#22c55e',
  BL: '#eab308'
};

// Cache rotated icons so we don't redraw canvas repeatedly for the same heading
const rotatedIconCache = {};

const dashboardDeviceCache = {
  user: null,
  devices: null,
  deviceRows: null,
  stats: null,
  statusKey: ''
};

const dashboardAlertCache = {
  user: null,
  distribution: null,
  loaded: false
};

const dashboardMapCache = {
  user: null,
  cars: null,
  loaded: false
};

const getRotatedCarIcon = (baseImgSrc, headingDeg) => {
  const rounded = Math.round((headingDeg || 0) / 5) * 5; // bucket into 5° steps
  const cacheKey = `${baseImgSrc}-${rounded}`;

  if (rotatedIconCache[cacheKey]) {
    return rotatedIconCache[cacheKey]; // Promise<dataURL>
  }

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const size = 80;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      ctx.translate(size / 2, size / 2);
      ctx.rotate((rounded * Math.PI) / 180);
      ctx.drawImage(img, -size / 2, -size / 2, size, size);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      resolve(canvas.toDataURL());
    };
    img.src = baseImgSrc;
  });

  rotatedIconCache[cacheKey] = promise;
  return promise;
};

const toUtcStamp = dt => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mm = String(dt.getUTCMinutes()).padStart(2, '0');
  const ss = String(dt.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${d}${hh}${mm}${ss}`;
};

const buildPresetRange = preset => {
  const now = new Date();
  let durationMs = 4 * 60 * 60 * 1000;
  if (preset === '12h') durationMs = 12 * 60 * 60 * 1000;
  if (preset === '24h') durationMs = 24 * 60 * 60 * 1000;
  if (preset === '7d') durationMs = 7 * 24 * 60 * 60 * 1000;
  const start = new Date(now.getTime() - durationMs);
  return { start, end: now, label: preset === '4h' ? 'Last 4 Hours' : preset === '12h' ? 'Last 12 Hours' : preset === '24h' ? 'Last 24 Hours' : preset === '7d' ? 'Last 7 Days' : 'Custom Range' };
};

export default function Dashboard({ onLogout, onTrack, initialPage = 'dashboard', onPageChange }) {
  const [page, setPage] = useState(initialPage);
  useEffect(() => {
    setPage(initialPage);
  }, [initialPage]);
  const user = useMemo(() => localStorage.getItem('authUser') || 'p@gmail.com', []);
  const hasDeviceCache = dashboardDeviceCache.user === user;
  const [devices, setDevices] = useState(() => hasDeviceCache ? dashboardDeviceCache.devices || [] : []);
  const [deviceRows, setDeviceRows] = useState(() => hasDeviceCache ? dashboardDeviceCache.deviceRows || [] : []);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [stats, setStats] = useState(() => hasDeviceCache ? dashboardDeviceCache.stats || { total: 0, running: 0, idle: 0 } : { total: 0, running: 0, idle: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 767 : false);
  const [isTablet, setIsTablet] = useState(typeof window !== 'undefined' ? (window.innerWidth > 767 && window.innerWidth <= 1024) : false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  const [fullWidth, setFullWidth] = useState(false);
  const [backAction, setBackAction] = useState(null);
  const mapInstance = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  // Google Maps API key now provided centrally via useFleetGoogleMaps
  const [carLocations, setCarLocations] = useState(() => dashboardMapCache.user === user ? dashboardMapCache.cars || [] : []);


  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [alertDistribution, setAlertDistribution] = useState(() => dashboardAlertCache.user === user ? dashboardAlertCache.distribution || [] : []);
  const [alertChartLoading, setAlertChartLoading] = useState(false);
  const [alertChartError, setAlertChartError] = useState('');
  const [selectedAlertSlice, setSelectedAlertSlice] = useState(null);
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
  const pieRef = useRef(null);
  const [legendCompact, setLegendCompact] = useState(false);
  const alertApiKey = useMemo(
    () =>
      process.env.REACT_APP_API_KEY ||
      (typeof window !== 'undefined' && window.REACT_APP_API_KEY) ||
      '',
    []
  );
  const { isLoaded } = useFleetGoogleMaps();
  const dashboardMapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeId: 'roadmap',
    backgroundColor: '#ffffff',
    clickableIcons: false,
    gestureHandling: 'greedy',
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false
  }), []);
  const handleMapLoad = useCallback((map) => {
    mapInstance.current = map;
    setMapReady(true);
  }, []);
  const handleMapUnmount = useCallback(() => {
    mapInstance.current = null;
    setMapReady(false);
  }, []);

  const dashboardAlertRange = useMemo(() => buildPresetRange('4h'), []);

  React.useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 767);
      setIsTablet(w > 767 && w <= 1024);
    };
    window.addEventListener('resize', onResize);
    // Initialize state
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setFullWidth(false);
    setBackAction(null);

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const shouldUseMobileSidebar = viewportWidth <= 1024;

    if (shouldUseMobileSidebar) {
      window.requestAnimationFrame(() => {
        setSidebarOpen(false);
        setSidebarHover(false);
      });
    }

    if (onPageChange) onPageChange(newPage);
  };



  useEffect(() => {
    if (page === 'dashboard' && mapReady && mapInstance.current && window.google?.maps) {
      window.google.maps.event.trigger(mapInstance.current, 'resize');
    }
  }, [page, mapReady, sidebarOpen, isMobile, isTablet]);

  useEffect(() => {
    setSelectedAlertSlice(null);
  }, [alertDistribution]);

  useEffect(() => {
    // .dashboard-alerts-card height is always 60vh, so we can derive its
    // actual pixel height directly from window.innerHeight — no ambiguity
    // from padding/box-sizing/observer box models.
    const CARD_HEIGHT_VH = 0.6;
    const THRESHOLD = 480; // px — below this card height, legend goes horizontal

    const checkHeight = () => {
      const cardHeight = window.innerHeight * CARD_HEIGHT_VH;
      setLegendCompact(cardHeight < THRESHOLD);
    };

    checkHeight();
    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, []);
  useEffect(() => {
    if (page !== 'dashboard') {
      return;
    }

    const imeis = devices
      .map(d => String(d.imei || '').trim())
      .filter(Boolean);

    if (imeis.length === 0) {
      setAlertDistribution([]);
      setAlertChartError('No devices available');
      return;
    }

    if (dashboardAlertCache.user === user && dashboardAlertCache.loaded && refreshFlag === 0) {
      setAlertDistribution(dashboardAlertCache.distribution || []);
      return;
    }

    let cancelled = false;

    const fetchAlerts = async () => {
      setAlertChartLoading(true);
      setAlertChartError('');
      try {
        const fromStamp = toUtcStamp(dashboardAlertRange.start);
        const toStamp = toUtcStamp(dashboardAlertRange.end);
        const url = `${process.env.REACT_APP_VEHICLE_ALERT_API_BASE}/alerts?imeis=${encodeURIComponent(
          imeis.join(',')
        )}&TimeRange=${fromStamp},${toStamp}`;

        const res = await fetch(url, {
          headers: alertApiKey ? { 'x-api-key': alertApiKey } : undefined,
        });

        if (!res.ok) {
          throw new Error(`Alerts API responded with ${res.status}`);
        }

        let json;
        try {
          json = await res.json();
        } catch {
          json = {};
        }

        let data = [];
        if (Array.isArray(json)) {
          data = json;
        } else if (Array.isArray(json.data)) {
          data = json.data;
        } else if (Array.isArray(json.processedAlerts)) {
          data = json.processedAlerts;
        } else if (json.body) {
          try {
            const parsed = JSON.parse(json.body);
            if (Array.isArray(parsed)) {
              data = parsed;
            } else if (Array.isArray(parsed.data)) {
              data = parsed.data;
            } else if (Array.isArray(parsed.processedAlerts)) {
              data = parsed.processedAlerts;
            }
          } catch {
            data = [];
          }
        }

        const distribution = ALERT_TYPE_ORDER.map(code => {
          const count = data.reduce((acc, raw) => {
            const codeValue =
              raw.alertCode ||
              raw.alertType ||
              raw.alert_type ||
              raw.type ||
              (typeof raw.packet_data === 'string'
                ? raw.packet_data.split(',')[4]
                : null);
            return acc + (String(codeValue || '').trim() === code ? 1 : 0);
          }, 0);
          return {
            code,
            label: ALERT_TYPES[code],
            count,
          };
        });

        if (!cancelled) {
          setAlertDistribution(distribution);
          dashboardAlertCache.user = user;
          dashboardAlertCache.distribution = distribution;
          dashboardAlertCache.loaded = true;
        }
      } catch (error) {
        console.error('[Dashboard] Failed to load alerts', error);
        if (!cancelled) {
          setAlertChartError('Failed to load alerts');
          setAlertDistribution([]);
        }
      } finally {
        if (!cancelled) {
          setAlertChartLoading(false);
        }
      }
    };

    fetchAlerts();
    return () => {
      cancelled = true;
    };
  }, [page, devices, alertApiKey, dashboardAlertRange, refreshFlag, user]);

  // Get latest device data and store it in state
  // NOTE: when you add your API, you can pass a placeholder prompt of 'false' here.
  // Example usage: fetch(yourUrl, { method: 'POST', body: JSON.stringify({ prompt: 'false' })
  const loadDevices = useCallback(async (force = false) => {
    if (!force && dashboardDeviceCache.user === user && dashboardDeviceCache.devices) {
      setDevices(dashboardDeviceCache.devices);
      setDeviceRows(dashboardDeviceCache.deviceRows || dashboardDeviceCache.devices);
      setStats(dashboardDeviceCache.stats || { total: dashboardDeviceCache.devices.length, running: 0, idle: 0 });
      setLoading(false);
      return;
    }
    let cancel = false;
    setLoading(true);
    try {
      // Always get latest user and API key
      const currentUser = localStorage.getItem('authUser') || user;
      const apiKey = process.env.REACT_APP_API_KEY || (window && window.REACT_APP_API_KEY) || '';
      // Add timestamp to URL to force fresh response
      const ts = Date.now();
      const url = `${process.env.REACT_APP_POST_DEVICE_BASE}?TableName=TabUserDetails&username=${encodeURIComponent(currentUser)}&_=${ts}`;

      const res = await fetch(url, {
        headers: { 'x-api-key': apiKey },
        cache: 'reload'
      });
      const json = await res.json();
      if (cancel) return;
      const list = (json && json.data && json.data.devices) || [];
      const normalized = Array.isArray(list) ? list.map((d, i) => {
        const batteryValue = d.batteryPct ?? d.batteryLevel ?? d.battery ?? d.batteryStatus;
        const statusValue = d.status || d.taxiStatus || (Number(d.speedKm ?? d.speed ?? 0) > 0 ? 'Online' : 'Offline');
        return {
          id: d.id || d.deviceId || d.imei || `device-${i}`,
          name: d.deviceName || d.name || d.deviceType || 'Device',
          imei: d.imei || d.IMEI || '',
          deviceType: d.deviceType || d.type || '',
          timestamp: d.ts || d.timestamp || d.lastSeen || d.lastUpdate || d.updatedAt,
          address: d.address || d.location || d.lastKnownLocation || d.addressLine || '-',
          speedKm: d.speedKm ?? d.speed ?? d.speedKph ?? d.speedKmh ?? '-',
          mileageKm: d.mileageKm ?? d.odometerKm ?? d.mileage ?? '-',
          parkedFor: d.parkedFor || d.parkingDuration || d.parking_duration || '-',
          battery: batteryValue === undefined || batteryValue === null ? '-' : batteryValue,
          taxiStatus: statusValue,
          status: statusValue,
        };
      }) : [];

      setDevices(normalized);
      setDeviceRows(normalized);
      setStats(s => ({ ...s, total: normalized.length }));
      dashboardDeviceCache.devices = normalized;
      dashboardDeviceCache.deviceRows = normalized;
      dashboardDeviceCache.stats = { total: normalized.length, running: 0, idle: 0 };
      dashboardDeviceCache.user = user;
      if (normalized.length === 0) toast.info('No devices registered for this account');
    } catch (err) {
      setDevices([]);
      setStats(s => ({ ...s, total: 0 }));
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    loadDevices(refreshFlag > 0);
  }, [loadDevices, refreshFlag]);

  const statusDeviceKey = useMemo(
    () => devices.map(device => String(device.imei || '').trim()).filter(Boolean).join(','),
    [devices]
  );

  useEffect(() => {
    if (dashboardDeviceCache.user === user && dashboardDeviceCache.statusKey === statusDeviceKey && refreshFlag === 0) {
      return;
    }

    let cancel = false;
    const buildRange = () => {
      const now = new Date();
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const sy = start.getFullYear();
      const sm = String(start.getMonth() + 1).padStart(2, '0');
      const sd = String(start.getDate()).padStart(2, '0');
      const ey = now.getFullYear();
      const em = String(now.getMonth() + 1).padStart(2, '0');
      const ed = String(now.getDate()).padStart(2, '0');
      return `${sy}${sm}${sd}000000,${ey}${em}${ed}235959`;
    };
    const range = buildRange();
    const fetchStatuses = async () => {
      setLoadingActivity(true);
      try {
        const results = await Promise.all(devices.map(async (dev) => {
          const url = `${process.env.REACT_APP_TRACKCURRENT_API_BASE}?imeis=${encodeURIComponent(dev.imei)}`;
          const res = await fetch(url);
          const json = await res.json();
          const arr = Array.isArray(json?.data) ? json.data : [];
          const last = arr.reduce((a, b) => (a && a.time_stamp > b.time_stamp ? a : b), null) || arr[arr.length - 1];
          const sp = Number(last?.speed);
          const status = (Number.isFinite(sp) && sp > 3) ? 'Running' : 'Idle';
          const odoRaw = Number(last?.ODO);
          const odometerKm = Number.isFinite(odoRaw) ? (odoRaw / 1000) : null;
          return { ...dev, status, lastUpdate: last?.time_stamp, odometerKm };
        }));
        if (cancel) return;

        const runningCount = results.filter(r => r.status === 'Running').length;
        const idleCount = results.length - runningCount;

        setDeviceRows(results);
        setStats(s => ({ ...s, running: runningCount, idle: idleCount }));
        dashboardDeviceCache.deviceRows = results;
        dashboardDeviceCache.stats = { total: results.length, running: runningCount, idle: idleCount };
        dashboardDeviceCache.statusKey = statusDeviceKey;
        dashboardDeviceCache.user = user;
      } catch {
        if (!cancel) setStats(s => ({ ...s, running: 0, idle: 0 }));
        toast.error('Failed to fetch device statuses');
      } finally {
        if (!cancel) setLoadingActivity(false);
      }
    };
    if (devices.length) {
      fetchStatuses();
      return () => { cancel = true; };
    } else {
      setStats(s => ({ ...s, running: 0, idle: 0 }));
      setLoadingActivity(false);
      return () => { cancel = true; };
    }
  }, [statusDeviceKey, refreshFlag, devices, user]);

  const statusApiUrl = process.env.REACT_APP_TRACKCURRENT_API_BASE || `${process.env.REACT_APP_TRACK_API_BASE}/testing3`;

  const buildMapRange = useCallback(() => {
    const now = new Date();
    const start = new Date(now.getTime() - 4 * 60 * 60 * 1000);
    const toStamp = (dt) => {
      const y = dt.getUTCFullYear();
      const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dt.getUTCDate()).padStart(2, '0');
      const hh = String(dt.getUTCHours()).padStart(2, '0');
      const mm = String(dt.getUTCMinutes()).padStart(2, '0');
      const ss = String(dt.getUTCSeconds()).padStart(2, '0');
      return `${y}${m}${d}${hh}${mm}${ss}`;
    };
    return `${toStamp(start)},${toStamp(now)}`;
  }, []);
  const latestPointToCar = useCallback((latest, imei, deviceType = '') => {

    const convertGps = (value, type = 'lat') => {
      const num = Number(value);

      if (!Number.isFinite(num)) {
        return null;
      }
      // Reject exact zero explicitly — treated as "no fix", not a real coordinate
      if (num === 0) return null;


      // If already proper decimal coordinates
      if (
        (type === 'lat' && num >= -90 && num <= 90) ||
        (type === 'lng' && num >= -180 && num <= 180)
      ) {
        return num;
      }

      // Convert GPS tracker DDMM.MMMM format to decimal
      const degrees = Math.floor(num / 100);
      const minutes = num - (degrees * 100);

      return degrees + (minutes / 60);
    };

    const lat = convertGps(
      latest?.latitude ?? latest?.lat,
      'lat'
    );

    const lng = convertGps(
      latest?.longitude ?? latest?.lng,
      'lng'
    );


    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    const heading = Number(latest.heading ?? 0);

    return {
      imei,

      name:
        latest.deviceName ||
        latest.device_name ||
        latest.name ||
        `Device ${imei}`,

      status:
        latest.status ||
        latest.taxiStatus ||
        latest.state ||
        '',

      deviceType,

      lat,
      lng,
      heading: Number.isFinite(heading) ? heading : 0,

      ts:
        latest.time_stamp ||
        latest.lastSeen ||
        latest.updatedAt ||
        ''
    };
  }, []);

  const fetchCarLocations = useCallback(async () => {
    try {
      const targets = devices.filter(d => d.imei);
      if (!targets.length) {
        setCarLocations([]);
        return;
      }

      const apiKey = process.env.REACT_APP_API_KEY || (typeof window !== 'undefined' && window.REACT_APP_API_KEY) || '';
      const imeiList = targets.map(d => d.imei);
      const deviceMetaByImei = Object.fromEntries(targets.map(d => [d.imei, d]));
      const imeisParam = imeiList.join(',');
      const url = `${statusApiUrl}?imeis=${encodeURIComponent(imeisParam)}`;
      const res = await fetch(url, { headers: apiKey ? { 'x-api-key': apiKey } : undefined, cache: 'no-store' });
      if (!res.ok) {
        setCarLocations([]);
        return;
      }

      const json = await res.json();
      let payload = json;
      if (typeof payload?.body === 'string') {
        try { payload = JSON.parse(payload.body); } catch { payload = json; }
      }
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      if (!list.length) {
        setCarLocations([]);
        return;
      }
      // NEW: filter out invalid/zero coordinates before picking latest per IMEI
      const validList = list.filter(r => {
        const lat = r?.latitude ?? r?.lat;
        const lng = r?.longitude ?? r?.lng;
        return lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng));
      });

      const latestByImei = {};
      for (const point of validList) {   // CHANGED: was `list`, now `validList`
        const imei = point?.imei || point?.IMEI;
        if (!imei) continue;
        const existing = latestByImei[imei];
        if (!existing || (point.time_stamp && point.time_stamp > existing.time_stamp)) {
          latestByImei[imei] = point;
        }
      }

      const cars = [];
      for (const imei of imeiList) {
        const latest = latestByImei[imei];
        if (!latest) continue;
        const car = latestPointToCar(latest, imei, deviceMetaByImei[imei]?.deviceType || '');
        if (car) cars.push(car);
      }
      setCarLocations(cars);
      dashboardMapCache.user = user;
      dashboardMapCache.cars = cars;
      dashboardMapCache.loaded = true;
    } catch (error) {
      console.error('Failed to load car locations', error);
      setCarLocations([]);
    }
  }, [devices, buildMapRange, latestPointToCar]);

  useEffect(() => {
    if (dashboardMapCache.user === user && dashboardMapCache.loaded && refreshFlag === 0) {
      setCarLocations(dashboardMapCache.cars || []);
      return;
    }
    fetchCarLocations();
  }, [refreshFlag, fetchCarLocations, user]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  }, []);

  const parseTimestampToMs = (ts) => {
    if (!ts) return NaN;
    const s = String(ts).trim();
    if (s.length !== 14) return NaN;
    const year = Number(s.slice(0, 4));
    const month = Number(s.slice(4, 6)) - 1;
    const day = Number(s.slice(6, 8));
    const hour = Number(s.slice(8, 10));
    const minute = Number(s.slice(10, 12));
    const second = Number(s.slice(12, 14));
    return Date.UTC(year, month, day, hour, minute, second);
  };

  const isActiveWithinHours = useCallback((car, hours = 4) => {
    const tsMs = parseTimestampToMs(car.ts);
    if (!Number.isFinite(tsMs)) return false;
    return (Date.now() - tsMs) <= hours * 60 * 60 * 1000;
  }, []);

  const activeCarLocations = useMemo(() => {
    return carLocations.filter((car) => isActiveWithinHours(car, 4));
  }, [carLocations, isActiveWithinHours]);

  const renderCarMarkers = useCallback(async () => {
    if (!window.google?.maps || !mapInstance.current) return;
    clearMarkers();

    const bounds = new window.google.maps.LatLngBounds();
    let boundsSet = false;

    for (const car of activeCarLocations) {
      const position = { lat: car.lat, lng: car.lng };
      bounds.extend(position);
      boundsSet = true;

      const rotatedIconUrl = await getRotatedCarIcon(TruckIconImg, car.heading);

      const marker = new window.google.maps.Marker({
        position,
        map: mapInstance.current,
        title: car.name,
        icon: {
          url: rotatedIconUrl,
          scaledSize: new window.google.maps.Size(80, 80),
          anchor: new window.google.maps.Point(40, 40)
        }
      });

      marker.addListener('click', () => {
        if (!infoWindowRef.current) {
          infoWindowRef.current = new window.google.maps.InfoWindow();
        }
        infoWindowRef.current.setContent(`
        <div style="padding:8px; min-width:160px;">
          <strong>${car.name}</strong><br/>
          ${car.imei ? `IMEI: ${car.imei}<br/>` : ''}
          ${car.status ? `Status: ${car.status}<br/>` : ''}
          ${car.ts ? `Last seen: ${new Date(parseTimestampToMs(car.ts)).toLocaleString()}<br/>` : ''}
        </div>
      `);
        infoWindowRef.current.open({ anchor: marker, map: mapInstance.current });
      });

      markersRef.current.push(marker);
    }

    if (boundsSet && !bounds.isEmpty()) {
      if (mapInstance.current) {
        if (activeCarLocations.length === 1) {
          mapInstance.current.setCenter(bounds.getCenter());
          mapInstance.current.setZoom(14);
        } else {
          mapInstance.current.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
        }
        // Trigger resize after setting bounds
        if (window.google?.maps?.event) {
          window.google.maps.event.trigger(mapInstance.current, 'resize');
        }
      }
    }
  }, [activeCarLocations, clearMarkers]);

  useEffect(() => {
    if (mapReady && mapInstance.current) {
      if (activeCarLocations.length > 0) {
        renderCarMarkers();
      } else {
        clearMarkers();
        if (mapInstance.current) {
          mapInstance.current.setCenter(DEFAULT_MAP_CENTER);
          mapInstance.current.setZoom(DEFAULT_MAP_ZOOM);
        }
      }
    }
  }, [mapReady, renderCarMarkers]);



  const total = stats.total;
  const running = stats.running;
  const idle = stats.idle;

  const logout = () => {
    localStorage.removeItem('authUser');
    if (onLogout) onLogout();
  };

  const handleFullWidthChange = (isFull) => {
    setFullWidth(isFull);
    if (isFull) {
      setSidebarOpen(false);
    }
  };

  const handleSidebarMouseEnter = () => {
    if (!isMobile && !isTablet) setSidebarHover(true);
  };

  const handleSidebarMouseLeave = () => {
    if (!isMobile && !isTablet) setSidebarHover(false);
  };

  const handlePieHover = (e) => {
    if (!pieRef.current || alertDistribution.length === 0) return;

    const totalAlerts = alertDistribution.reduce((acc, d) => acc + d.count, 0);
    if (!totalAlerts) return;

    const rect = pieRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;

    const radius = rect.width / 2;
    const distFromCenter = Math.sqrt(dx * dx + dy * dy);
    const innerRadius = 65;
    if (distFromCenter < innerRadius || distFromCenter > radius) {
      setSelectedAlertSlice(null);
      return;
    }

    // Calculate angle for positioning popup outside the circle
    let angleDeg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    if (angleDeg < 0) angleDeg += 360;

    // Position popup outside the pie circle
    const angleRad = (angleDeg * Math.PI) / 180;
    const pieWrapperRect = pieRef.current.parentElement.getBoundingClientRect();
    const pieCenterX = pieWrapperRect.width / 2;
    const pieCenterY = pieWrapperRect.height / 2;

    // Place popup at radius + 50px offset from center
    const popupDistance = radius + 50;
    const popupX = pieCenterX + Math.sin(angleRad) * popupDistance;
    const popupY = pieCenterY - Math.cos(angleRad) * popupDistance;

    setPopupPos({ x: popupX, y: popupY });

    const clickPct = (angleDeg / 360) * 100;

    let cumulativePct = 0;
    for (const item of alertDistribution) {
      const pct = (item.count / totalAlerts) * 100;
      const start = cumulativePct;
      const end = cumulativePct + pct;
      if (clickPct >= start && clickPct < end) {
        setSelectedAlertSlice({
          code: item.code,
          label: item.label,
          count: item.count,
          percent: pct.toFixed(1),
        });
        return;
      }
      cumulativePct = end;
    }
  };

  React.useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarHover(false);
    }
  }, [isMobile, isTablet]);

  return (
    <div className={`dash-root ${sidebarOpen ? 'sidebar-open' : ''} ${fullWidth ? 'fullwidth' : ''} dash-page-${page}`}>
      {loading && (
        <div className="spinner-overlay"><div className="spinner" /></div>
      )}

      <div
        className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside
        className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarHover ? 'hover' : ''} ${fullWidth ? 'fixed-sidebar' : ''}`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div className="brand-row">
          <img src={MileEdgeLogo} alt="MileEdge" className="brand-img" />
          <img src={MileEdgeTextLogo} alt="MileEdge Text" className="brand-text" />
        </div>

        <nav className="nav-list">
          <button
            title="Dashboard"
            aria-label="Dashboard"
            className={`nav ${page === 'dashboard' ? 'active' : ''}`}
            onClick={() => handlePageChange('dashboard')}
          >
            <MdHome className="nav-icon" />
            <span className="nav-text">Dashboard</span>
          </button>
          <button
            title="Devices"
            aria-label="Devices"
            className={`nav ${page === 'devices' ? 'active' : ''}`}
            onClick={() => handlePageChange('devices')}
          >
            <MdDevices className="nav-icon" />
            <span className="nav-text">Devices</span>
          </button>
          <button
            title="Trip Report"
            aria-label="Trip Report"
            className={`nav ${page === 'tripreport' ? 'active' : ''}`}
            onClick={() => handlePageChange('tripreport')}
          >
            <MdRoute className="nav-icon" />
            <span className="nav-text">Trip Report</span>
          </button>
          <button
            title="Assign Route"
            aria-label="Assign Route"
            className={`nav ${page === 'assigned-route' ? 'active' : ''}`}
            onClick={() => handlePageChange('assigned-route')}
          >
            <MdAssignment className="nav-icon" />
            <span className="nav-text">Assign Route</span>
          </button>
          <button
            title="Relay Control"
            aria-label="Relay Control"
            className={`nav ${page === 'relay-control' ? 'active' : ''}`}
            onClick={() => handlePageChange('relay-control')}
          >
            <MdPowerSettingsNew className="nav-icon" />
            <span className="nav-text">Relay Control</span>
          </button>
          <button
            title="Geofencing"
            aria-label="Geofencing"
            className={`nav ${page === 'geofencing' ? 'active' : ''}`}
            onClick={() => handlePageChange('geofencing')}
          >
            <MdLocationOn className="nav-icon" />
            <span className="nav-text">Geofencing</span>
          </button>
          <button
            title="Alerts"
            aria-label="Alerts"
            className={`nav ${page === 'alerts' ? 'active' : ''}`}
            onClick={() => handlePageChange('alerts')}
          >
            <MdNotifications className="nav-icon" />
            <span className="nav-text">Alerts</span>
          </button>
          <button
            title="Device Monitor"
            aria-label="Device Monitor"
            className={`nav ${page === 'device-monitor' ? 'active' : ''}`}
            onClick={() => handlePageChange('device-monitor')}
          >
            <MdMonitorHeart className="nav-icon" />
            <span className="nav-text">Device Monitor</span>
          </button>
          <button
            title="CAN Telemetry"
            aria-label="CAN Telemetry"
            className={`nav ${page === 'can-telemetry' ? 'active' : ''}`}
            onClick={() => handlePageChange('can-telemetry')}
          >
            <MdSignalCellularAlt className="nav-icon" />
            <span className="nav-text">CAN Telemetry</span>
          </button>
          <button
            title="Maintenance"
            aria-label="Maintenance"
            className={`nav ${page === 'maintenance' ? 'active' : ''}`}
            onClick={() => handlePageChange('maintenance')}
          >
            <MdBuild className="nav-icon" />
            <span className="nav-text">Maintenance</span>
          </button>
          <button
            title="Documents"
            aria-label="Documents"
            className={`nav ${page === 'documents' ? 'active' : ''}`}
            onClick={() => handlePageChange('documents')}
          >
            <MdDescription className="nav-icon" />
            <span className="nav-text">Documents</span>
          </button>
          <button
            title="Support"
            aria-label="Support"
            className={`nav ${page === 'support' ? 'active' : ''}`}
            onClick={() => handlePageChange('support')}
          >
            <MdSupportAgent className="nav-icon" />
            <span className="nav-text">Support</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="logout" onClick={logout} title="Logout" aria-label="Logout">
            <MdLogout className="nav-icon" />
            <span className="nav-text">Logout</span>
          </button>
        </div>
      </aside>

      <div className="content">
        <div className="page-content-shell">
          <header
            className={`topbar ${isMobile ? 'mobile-sticky' : ''} ${page === 'dashboard' ? '' : 'single'}`}
          >
            {isMobile ? (
              <div className="mobile-topbar-layout">
                {backAction ? (
                  <button className="hamburger-btn" onClick={backAction}>←</button>
                ) : (
                  <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}>☰</button>
                )}
                <div className="mobile-logo-wrapper">
                  <img
                    src={process.env.PUBLIC_URL + '/mileedge-mobile.png'}
                    alt="MileEdge"
                    className="mobile-header-logo"
                  />
                </div>
                {page === 'dashboard' && (
                  <div className="mobile-profile-icon">
                    <div className="user-chip user-chip--sm">
                      <span className="user-initial">
                        {String(user || '?').trim().slice(0, 1).toUpperCase()}
                      </span>
                      <span className="user-expand-name">{user}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="topbar-left">
                  {backAction ? (
                    <button className="hamburger-btn" onClick={backAction}>←</button>
                  ) : isTablet ? (
                    <button className="hamburger-btn" onClick={toggleSidebar}>☰</button>
                  ) : null}
                </div>

                <div className="topbar-center">
                  {page === 'dashboard' && (
                    <div className="stats-row">
                      <div className="stat-card total">
                        <div className="stat-label">Total</div>
                        <div className="stat-value-row">
                          <span className="dot" />
                          <span className="stat-value">{loading ? '...' : total}</span>
                        </div>
                      </div>
                      <div className="stat-card running">
                        <div className="stat-label">Running</div>
                        <div className="stat-value-row">
                          <span className="dot" />
                          <span className="stat-value">{loadingActivity ? '...' : running}</span>
                        </div>
                      </div>
                      <div className="stat-card idle">
                        <div className="stat-label">Idle</div>
                        <div className="stat-value-row">
                          <span className="dot" />
                          <span className="stat-value">{loadingActivity ? '...' : idle}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="topbar-right">
                  {page === 'dashboard' && (
                    <div className="user-chip">
                      <span className="user-initial">
                        {String(user || '?').trim().slice(0, 1).toUpperCase()}
                      </span>
                      <span className="user-expand-name">{user}</span>
                    </div>
                  )}
                </div>
              </>)}
          </header>

          <div className={page === 'dashboard' ? 'dashboard-widgets' : 'dashboard-widgets dashboard-widgets--hidden'}>
            <>
              {isMobile && (
                <div className="stats-row stats-row--mobile">
                  <div className="stat-card total">
                    <div className="stat-label">Total</div>
                    <div className="stat-value-row">
                      <span className="dot" />
                      <span className="stat-value">{loading ? '...' : total}</span>
                    </div>
                  </div>
                  <div className="stat-card running">
                    <div className="stat-label">Running</div>
                    <div className="stat-value-row">
                      <span className="dot" />
                      <span className="stat-value">{loadingActivity ? '...' : running}</span>
                    </div>
                  </div>
                  <div className="stat-card idle">
                    <div className="stat-label">Idle</div>
                    <div className="stat-value-row">
                      <span className="dot" />
                      <span className="stat-value">{loadingActivity ? '...' : idle}</span>
                    </div>
                  </div>
                </div>
              )}

              {(!isTablet || !sidebarOpen) && (
                <div className="dashboard-main-layout">
                  <section className="dashboard-alerts-panel">
                    <div className="dashboard-alerts-card">
                      <div className="dashboard-alerts-header">
                        <div>
                          <h3>Alert Distribution</h3>
                          <div className="dashboard-alerts-subtitle">
                            All IMEIs • {dashboardAlertRange.label}
                          </div>
                        </div>
                      </div>

                      {alertChartLoading ? (
                        <div className="dashboard-alerts-loading">
                          <div className="spinner-circle" />
                          <div>Loading alert distribution…</div>
                        </div>
                      ) : alertChartError ? (
                        <div className="dashboard-alerts-empty">{alertChartError}</div>
                      ) : alertDistribution.length === 0 ? (
                        <div className="dashboard-alerts-empty">
                          No alert data available for current devices.
                        </div>
                      ) : (
                        <>
                          {(() => {
                            const totalAlerts = alertDistribution.reduce((acc, d) => acc + d.count, 0);
                            let cumulativePct = 0;
                            const gradientStops = alertDistribution.map(item => {
                              const pct = totalAlerts ? (item.count / totalAlerts) * 100 : 0;
                              const start = cumulativePct;
                              const end = cumulativePct + pct;
                              cumulativePct = end;
                              return `${ALERT_COLORS[item.code] || '#6b7280'} ${start}% ${end}%`;
                            });
                            const pieBackground = totalAlerts
                              ? `conic-gradient(${gradientStops.join(', ')})`
                              : '#e5e7eb';

                            return (
                              <div className="alerts-chart-layout">
                                <div className="alerts-pie-wrapper">
                                  <div
                                    ref={pieRef}
                                    className="alerts-pie"
                                    style={{ background: pieBackground }}
                                    onMouseMove={handlePieHover}
                                    onMouseLeave={() => setSelectedAlertSlice(null)}
                                  >
                                    <div className="alerts-pie-center">
                                      <span className="alerts-pie-total">{totalAlerts}</span>
                                      <span className="alerts-pie-total-label">Total</span>
                                    </div>
                                  </div>

                                  {selectedAlertSlice && (
                                    <div
                                      className="alerts-pie-popup"
                                      style={{
                                        left: `${popupPos.x}px`,
                                        top: `${popupPos.y}px`,
                                        transform: 'translateX(-50%)'
                                      }}
                                    >
                                      <button
                                        className="alerts-pie-popup-close"
                                        onClick={() => setSelectedAlertSlice(null)}
                                        onMouseLeave={(e) => e.stopPropagation()}
                                      >
                                        ×
                                      </button>
                                      <span
                                        className="alerts-pie-popup-swatch"
                                        style={{ backgroundColor: ALERT_COLORS[selectedAlertSlice.code] || '#6b7280' }}
                                      />
                                      <div className="alerts-pie-popup-text">
                                        <div className="alerts-pie-popup-label">{selectedAlertSlice.label}</div>
                                        <div className="alerts-pie-popup-value">
                                          {selectedAlertSlice.count} alerts · {selectedAlertSlice.percent}%
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className={`alerts-legend ${legendCompact ? 'alerts-legend--horizontal' : ''}`}>
                                  {ALERT_TYPE_ORDER.map(code => {
                                    const row = alertDistribution.find(d => d.code === code);
                                    const count = row ? row.count : 0;
                                    const pct = totalAlerts ? ((count / totalAlerts) * 100).toFixed(1) : '0.0';
                                    return (
                                      <div key={code} className="alerts-legend-item">
                                        <span
                                          className="legend-swatch"
                                          style={{ backgroundColor: ALERT_COLORS[code] || '#6b7280' }}
                                        />
                                        <span className="legend-label">
                                          {code} – {ALERT_TYPES[code]}
                                        </span>
                                        <span className="legend-percent">{pct}%</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </section>

                  <div className="map-widget-wrapper">
                    <section className="map-widget">
                      <div className="map-body">
                        {isLoaded ? (
                          <GoogleMap
                            mapContainerClassName="dashboard-map"
                            center={DEFAULT_MAP_CENTER}
                            zoom={DEFAULT_MAP_ZOOM}
                            options={dashboardMapOptions}
                            onLoad={handleMapLoad}
                            onUnmount={handleMapUnmount}
                          />
                        ) : (
                          <div className="dashboard-map-loading" style={{ width: '100%', height: '100%', minHeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            Loading map...
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              )}
            </>
          </div>




          {page === 'add' && (
            <div className="add-view">
              <AddDevice
                onAdded={async (newDev) => {
                  try {
                    window.lastAddedDevice = newDev;
                    setLoading(true);
                    await loadDevices(true);
                  } finally {
                    setLoading(false);
                  }
                  handlePageChange('devices');
                }}
              />
            </div>
          )}

          {page === 'tripreport' && (
            <TripReport
              devices={deviceRows}
              onFullWidthChange={handleFullWidthChange}
              setBackAction={setBackAction}

              onTrack={onTrack}
            />
          )}

          {page === 'relay-control' && <RelayControl devices={deviceRows} />}

          {page === 'assigned-route' && (
            <AssignedRoute devices={deviceRows} user={user} />
          )}

          {page === 'geofencing' && (
            <Geofencing
              devices={deviceRows}
              user={user}
              onFullWidthChange={handleFullWidthChange}
              setBackAction={setBackAction}

            />
          )}

          {page === 'alerts' && (
            <Alerts
              devices={deviceRows}
              onFullWidthChange={handleFullWidthChange}
              setBackAction={setBackAction}

            />
          )}
          {page === 'devices' && (
            <Devices
              deviceRows={deviceRows}
              onTrack={onTrack}
              onAddDevice={() => handlePageChange('add')}
            />
          )}
          {page === 'device-monitor' && (
            <div className="content-static-slot content-static-slot--fill">
              <DeviceMonitor devices={devices} user={user} />
            </div>
          )}
          {page === 'can-telemetry' && (
            <div className="content-static-slot content-static-slot--fill">
              <CANTelemetry devices={deviceRows} user={user} />
            </div>
          )}
          {page === 'maintenance' && (
            <div className="content-static-slot content-static-slot--fill">
              <Maintenance devices={deviceRows} user={user} />
            </div>
          )}
          {page === 'documents' && <DocumentUploader />}
          {page === 'support' && (
            <Support user={user} onTicketCreated={() => setRefreshFlag((f) => f + 1)} />
          )}
        </div>
      </div>
    </div>
  );
}
